/**
 * 上传数据本地存储工具
 * 使用 IndexedDB 存储分片上传数据，支持大容量数据
 */
import { createLogger } from "./logger.js";

const logger = createLogger("UploadStorage");

const DB_VERSION = 2;
const STORE_NAME = "upload_data";
/** 分片 Blob 单独存储，key 为 partChunk:${fileId}:${partNumber} */
const STORE_PART_CHUNKS = "part_chunks";

class UploadStorage {
  constructor() {
    this.db = null;
    this.initPromise = null;
    /** 当前已打开的 DB 对应的 code，用于 code 切换时关闭并重开 */
    this.currentDbCode = null;
  }

  /**
   * 当前 code（从 localStorage 读取 lastAuthCode 或 lastShareCode）
   * @returns {string|null}
   */
  getCurrentCode() {
    return typeof localStorage !== "undefined"
      ? localStorage.getItem("lastAuthCode") || localStorage.getItem("lastShareCode")
      : null;
  }

  /**
   * 按 code 区分的 IndexedDB 名称（code 不同则 DB 不同，实现隔离）
   * @returns {string}
   */
  getDbName() {
    const code = this.getCurrentCode() || "default";
    const safe = String(code).replace(/[^a-zA-Z0-9_.-]/g, "_");
    return "upload_storage_" + (safe || "default");
  }

  /**
   * 初始化 IndexedDB（按当前 code 打开对应 DB；code 变化时先关闭再打开新 DB）
   */
  async init() {
    const currentCode = this.getCurrentCode();
    const dbName = this.getDbName();

    if (this.db && this.currentDbCode !== currentCode) {
      this.db.close();
      this.db = null;
      this.currentDbCode = null;
      this.initPromise = null;
    }

    if (this.db) {
      return this.db;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        logger.error("IndexedDB 不支持");
        reject(new Error("IndexedDB 不支持"));
        return;
      }

      const request = indexedDB.open(dbName, DB_VERSION);

      request.onerror = () => {
        logger.error("IndexedDB 打开失败:", request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.currentDbCode = currentCode;
        logger.info("IndexedDB 初始化成功", dbName);
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, {
            keyPath: "fileId",
          });
          objectStore.createIndex("uploadId", "uploadId", { unique: false });
          objectStore.createIndex("timestamp", "timestamp", { unique: false });
          logger.info("IndexedDB 对象存储创建成功");
        }
        if (!db.objectStoreNames.contains(STORE_PART_CHUNKS)) {
          // 分片 Blob 单独存储，无 keyPath，put 时传入 key：partChunk:fileId:partNumber
          db.createObjectStore(STORE_PART_CHUNKS);
          logger.info("IndexedDB part_chunks 存储创建成功");
        }
      };
    });

    return this.initPromise;
  }

  /**
   * 保存上传数据
   * @param {string} fileId - 文件ID
   * @param {Object} uploadData - 上传数据
   * @param {string} uploadData.uploadId - 上传ID
   * @param {Array} uploadData.partInfoList - 分片信息列表
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveUploadData(fileId, uploadData) {
    try {
      await this.init();

      if (!this.db) {
        logger.error("IndexedDB 不可用，无法保存上传数据");
        throw new Error("IndexedDB 不可用，无法保存上传数据");
      }

      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const objectStore = transaction.objectStore(STORE_NAME);

      const data = {
        fileId,
        uploadId: uploadData.uploadId,
        partInfoList: uploadData.partInfoList,
        timestamp: Date.now(),
      };

      const request = objectStore.put(data);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          logger.info(`上传数据已保存: fileId=${fileId}, uploadId=${uploadData.uploadId}`);
          resolve(true);
        };

        request.onerror = () => {
          logger.error("保存上传数据失败:", request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      logger.error("保存上传数据异常:", error);
      throw error;
    }
  }

  /**
   * 按分片逐个保存上传数据（含 chunk），每存完一个分片调用 onProgress；支持 onCancelCheck 在校验中取消
   * @param {string} fileId - 文件ID
   * @param {Object} uploadData - 上传数据（partInfoList 每项含 chunk）
   * @param {Function} [onProgress] - (completed, total) => void，每存完一个分片调用
   * @param {Function} [onCancelCheck] - () => boolean，为 true 时中止保存并 reject
   * @returns {Promise<boolean>}
   */
  async saveUploadDataWithChunks(fileId, uploadData, onProgress, onCancelCheck) {
    try {
      await this.init();
      if (!this.db) {
        logger.error("IndexedDB 不可用，无法保存上传数据");
        throw new Error("IndexedDB 不可用，无法保存上传数据");
      }

      const partInfoList = uploadData.partInfoList || [];
      const partInfoListForMeta = partInfoList.map(({ chunk, ...rest }) => rest);

      // 1. 先存元数据（无 chunk），很快
      const metaData = {
        fileId,
        uploadId: uploadData.uploadId,
        partInfoList: partInfoListForMeta,
        timestamp: Date.now(),
      };
      await new Promise((resolve, reject) => {
        const tx = this.db.transaction([STORE_NAME], "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.put(metaData);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
      logger.info(`上传元数据已保存: fileId=${fileId}, 分片数=${partInfoList.length}`);

      // 2. 按分片逐个存 chunk，每存完一个调 onProgress，并检查 onCancelCheck
      const total = partInfoList.length;
      for (let i = 0; i < total; i++) {
        if (typeof onCancelCheck === "function" && onCancelCheck()) {
          throw new Error("用户取消上传");
        }
        const part = partInfoList[i];
        const chunk = part?.chunk;
        if (chunk == null) continue;
        const partNumber = part.partNumber ?? part.part_number ?? i + 1;
        const key = `partChunk:${fileId}:${partNumber}`;
        await new Promise((resolve, reject) => {
          const tx = this.db.transaction([STORE_PART_CHUNKS], "readwrite");
          const store = tx.objectStore(STORE_PART_CHUNKS);
          const req = store.put(chunk, key);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        });
        if (typeof onProgress === "function") {
          onProgress(i + 1, total);
        }
      }
      logger.info(`分片 Blob 已全部保存: fileId=${fileId}`);
      return true;
    } catch (error) {
      logger.error("保存上传数据（含分片）异常:", error);
      throw error;
    }
  }

  /**
   * 检查该 fileId 的上传数据是否完整（part_chunks 中是否存在所有分片的 chunk）
   * 用于续传列表过滤：校验中断或刷新导致的不完整数据应移除
   * @param {string} fileId - 文件ID
   * @returns {Promise<boolean>} 是否完整
   */
  async isUploadDataComplete(fileId) {
    try {
      await this.init();
      if (!this.db) return false;

      const main = await new Promise((resolve, reject) => {
        const tx = this.db.transaction([STORE_NAME], "readonly");
        const req = tx.objectStore(STORE_NAME).get(fileId);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });

      if (!main?.partInfoList?.length) return false;

      // 同一事务内并发查所有 part chunk，避免循环 await 导致事务提前关闭
      const tx = this.db.transaction([STORE_PART_CHUNKS], "readonly");
      const chunkStore = tx.objectStore(STORE_PART_CHUNKS);
      const results = await Promise.all(
        main.partInfoList.map((part, i) => {
          const partNumber = part.partNumber ?? part.part_number ?? i + 1;
          const key = `partChunk:${fileId}:${partNumber}`;
          return new Promise((resolve, reject) => {
            const req = chunkStore.get(key);
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
          });
        })
      );
      return results.every((chunk) => chunk != null);
    } catch (error) {
      logger.error("检查上传数据完整性异常:", error);
      return false;
    }
  }

  /**
   * 获取上传数据（含从 part_chunks 恢复的 chunk，用于刷新后续传）
   * @param {string} fileId - 文件ID
   * @returns {Promise<Object|null>} 上传数据，partInfoList 中每项会带上 chunk
   */
  async getUploadData(fileId) {
    try {
      await this.init();

      if (!this.db) {
        logger.error("IndexedDB 不可用，无法获取上传数据");
        return null;
      }

      const tx = this.db.transaction([STORE_NAME, STORE_PART_CHUNKS], "readonly");
      const mainStore = tx.objectStore(STORE_NAME);
      const chunkStore = tx.objectStore(STORE_PART_CHUNKS);

      const main = await new Promise((resolve, reject) => {
        const req = mainStore.get(fileId);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });

      if (!main || !main.partInfoList || main.partInfoList.length === 0) {
        logger.info(`未找到上传数据: fileId=${fileId}`);
        return main ?? null;
      }

      // 按 part 逐个取回 chunk 并挂到 partInfoList 上
      const partInfoList = main.partInfoList;
      await Promise.all(
        partInfoList.map((part, index) => {
          const partNumber = part.partNumber ?? part.part_number ?? index + 1;
          const key = `partChunk:${fileId}:${partNumber}`;
          return new Promise((resolve, reject) => {
            const req = chunkStore.get(key);
            req.onsuccess = () => {
              partInfoList[index].chunk = req.result;
              resolve();
            };
            req.onerror = () => reject(req.error);
          });
        })
      );
      logger.info(`上传数据已获取（含 ${partInfoList.length} 个分片）: fileId=${fileId}`);
      return main;
    } catch (error) {
      logger.error("获取上传数据异常:", error);
      return null;
    }
  }

  /**
   * 删除上传数据（含该 fileId 下所有分片 chunk）
   * @param {string} fileId - 文件ID
   * @returns {Promise<boolean>} 是否删除成功
   */
  async deleteUploadData(fileId) {
    try {
      await this.init();

      if (!this.db) {
        logger.error("IndexedDB 不可用，无法删除上传数据");
        return false;
      }

      const main = await new Promise((resolve, reject) => {
        const tx = this.db.transaction([STORE_NAME], "readonly");
        const req = tx.objectStore(STORE_NAME).get(fileId);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });

      const tx = this.db.transaction([STORE_NAME, STORE_PART_CHUNKS], "readwrite");
      const mainStore = tx.objectStore(STORE_NAME);
      const chunkStore = tx.objectStore(STORE_PART_CHUNKS);

      mainStore.delete(fileId);
      if (main?.partInfoList?.length) {
        for (const part of main.partInfoList) {
          const partNumber = part.partNumber ?? part.part_number;
          if (partNumber != null) {
            chunkStore.delete(`partChunk:${fileId}:${partNumber}`);
          }
        }
      }

      return new Promise((resolve, reject) => {
        tx.oncomplete = () => {
          logger.info(`上传数据已删除: fileId=${fileId}`);
          resolve(true);
        };
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      logger.error("删除上传数据异常:", error);
      return false;
    }
  }

  /**
   * 获取所有上传数据
   * @returns {Promise<Array>} 所有上传数据列表
   */
  async getAllUploadData() {
    try {
      await this.init();

      if (!this.db) {
        logger.error("IndexedDB 不可用，无法获取所有上传数据");
        return [];
      }

      const transaction = this.db.transaction([STORE_NAME], "readonly");
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.getAll();

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const results = request.result || [];
          // logger.info(JSON.stringify(results));
          logger.info(`获取所有上传数据: 共 ${results.length} 条`);
          resolve(results);
        };

        request.onerror = () => {
          logger.error("获取所有上传数据失败:", request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      logger.error("获取所有上传数据异常:", error);
      return [];
    }
  }

  /**
   * 保存文件信息到本地存储（用于恢复文件列表）
   * @param {string} fileId - 文件ID（本地生成的ID）
   * @param {Object} fileInfo - 文件信息
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveFileInfo(fileId, fileInfo) {
    try {
      await this.init();

      if (!this.db) {
        logger.error("IndexedDB 不可用，无法保存文件信息");
        throw new Error("IndexedDB 不可用，无法保存文件信息");
      }

      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const objectStore = transaction.objectStore(STORE_NAME);

      // 获取现有数据或创建新数据
      const existingData = await new Promise((resolve, reject) => {
        const request = objectStore.get(fileId);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      const data = existingData || {
        fileId: fileId,
        uploadId: null,
        partInfoList: null,
        timestamp: Date.now(),
      };

      // 更新文件信息
      data.fileInfo = {
        id: fileInfo.id,
        name: fileInfo.name,
        size: fileInfo.size,
        type: fileInfo.type,
        addedAt: fileInfo.addedAt,
        uploadStatus: fileInfo.uploadStatus,
        progress: fileInfo.progress,
        uploadedSize: fileInfo.uploadedSize,
        downloadCount: fileInfo.downloadCount || 0,
      };

      const request = objectStore.put(data);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          logger.info(`文件信息已保存: fileId=${fileId}, name=${fileInfo.name}`);
          resolve(true);
        };

        request.onerror = () => {
          logger.error("保存文件信息失败:", request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      logger.error("保存文件信息异常:", error);
      throw error;
    }
  }

  /**
   * 删除文件信息（保留上传数据）
   * @param {string} fileId - 文件ID
   * @returns {Promise<boolean>} 是否删除成功
   */
  async deleteFileInfo(fileId) {
    try {
      await this.init();

      if (!this.db) {
        logger.error("IndexedDB 不可用，无法删除文件信息");
        return false;
      }

      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const objectStore = transaction.objectStore(STORE_NAME);

      // 获取现有数据
      const existingData = await new Promise((resolve, reject) => {
        const request = objectStore.get(fileId);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (existingData) {
        // 只删除 fileInfo，保留上传数据
        delete existingData.fileInfo;
        const request = objectStore.put(existingData);

        return new Promise((resolve, reject) => {
          request.onsuccess = () => {
            logger.info(`文件信息已删除: fileId=${fileId}`);
            resolve(true);
          };

          request.onerror = () => {
            logger.error("删除文件信息失败:", request.error);
            reject(request.error);
          };
        });
      }

      return true;
    } catch (error) {
      logger.error("删除文件信息异常:", error);
      return false;
    }
  }


  /**
   * 重置所有上传数据（清空所有存储）
   * @returns {Promise<boolean>} 是否重置成功
   */
  async reset() {
    try {
      await this.init();

      if (!this.db) {
        logger.error("IndexedDB 不可用，无法重置上传数据");
        throw new Error("IndexedDB 不可用，无法重置上传数据");
      }

      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.clear();

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          logger.info("所有上传数据已重置");
          resolve(true);
        };

        request.onerror = () => {
          logger.error("重置上传数据失败:", request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      logger.error("重置上传数据异常:", error);
      throw error;
    }
  }

  /**
   * 清理过期的上传数据（超过7天）
   * @returns {Promise<number>} 清理的数据数量
   */
  async cleanExpiredData() {
    try {
      await this.init();

      if (!this.db) {
        return 0;
      }

      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const objectStore = transaction.objectStore(STORE_NAME);
      const index = objectStore.index("timestamp");
      const range = IDBKeyRange.upperBound(sevenDaysAgo);
      const request = index.openCursor(range);

      let deletedCount = 0;

      return new Promise((resolve, reject) => {
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            logger.info(`清理过期数据完成，共删除 ${deletedCount} 条`);
            resolve(deletedCount);
          }
        };

        request.onerror = () => {
          logger.error("清理过期数据失败:", request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      logger.error("清理过期数据异常:", error);
      return 0;
    }
  }

  /**
   * 上传参数在 sessionStorage 中的 key，按 code 区分；不传 code 时从 localStorage 读取
   * @param {string} [code] - 可选，授权码/分享码；不传则用 getCurrentCode()
   * @returns {string} sessionStorage 的 key，如 'uploadParams_xxx' 或 'uploadParams'
   */
  getParamsKey(code) {
    const c = code !== undefined && code !== null && code !== "" ? code : this.getCurrentCode();
    return c ? "uploadParams_" + c : "uploadParams";
  }
}

// 创建单例
const uploadStorage = new UploadStorage();

// 导出单例和类
export default uploadStorage;
export { UploadStorage };

